import React from 'react';
import { Card, CardContent, CardMedia, CardActions, Typography, Button, Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import theme from '../../theme';
import { useTranslation } from 'react-i18next';

export interface HomePageCardProps {
  title: string;
  description: string;
  image: string;
  buttonText: string;
  route: string;
  imageAlt?: string;
  disabled?: boolean;
  disabledMessage?: string;
}

export const HomePageCard: React.FC<HomePageCardProps> = ({
  title,
  description,
  image,
  buttonText,
  route,
  imageAlt,
  disabled = false,
  disabledMessage
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleOnClick = () => {
    if (disabled) return;
    route.startsWith("/")
      ? navigate(route)
      : window.open(route, "_blank");
  };

  const disabledMessageId = disabled && disabledMessage ? `${title.replace(/\s+/g,'-').toLowerCase()}-disabled-reason` : undefined;

  return (
    <Card className={`homepage-card ${disabled ? 'homepage-card-disabled' : ''}`} aria-describedby={disabledMessageId}>
      <CardMedia
        component="img"
        image={image}
        alt={imageAlt || title}
        className="homepage-card-media"
      />
      <CardContent className="homepage-card-content">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography gutterBottom variant="h5" component="div" sx={{ mb: 0 }}>
            {title}
          </Typography>
          {disabled && disabledMessage && (
            <>
              <Tooltip title={disabledMessage} arrow placement="top">
                <Box
                  component="span"
                  sx={{
                    color: 'error.main',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px'
                  }}
                  role="img"
                  aria-label={t("Information about disabled state")}
                >
                    <Icon path={mdiInformationOutline} size={1} />
                </Box>
              </Tooltip>
              {disabledMessageId && <span id={disabledMessageId} style={{position:'absolute',left:'-10000px'}}>{disabledMessage}</span>}
            </>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </CardContent>
      <CardActions className="homepage-card-actions" sx ={{ background: theme.palette.primary.light }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleOnClick}
          disabled={disabled}
          aria-describedby={disabledMessageId}
        >
          {buttonText}
        </Button>
      </CardActions>
    </Card>
  );
};


